import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type {
	TemplateCreateBody,
	TemplateResponse,
	TemplateUpdateBody,
} from "~/shared/schemas/template-schema.ts";
import { templateResponseSchema } from "~/shared/schemas/template-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	postAndValidate,
	postAndValidateTraxAr,
	putAndValidate,
	putAndValidateTraxAr,
} from "~/shared/services/api/fetch-client.ts";
import type { ApiResult } from "~/shared/types/api-types.ts";
import { getIsInternal } from "~/shared/utils/get-deployment-type.ts";

export const useCreateTemplate = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useNotificationActions();

	return useMutation({
		mutationFn: async (
			body: TemplateCreateBody,
		): Promise<ApiResult<TemplateResponse>> => {
			const isInternal = getIsInternal();
			const route = isInternal
				? apiRoutes.TEMPLATES
				: apiRoutes.EXTERNALTEMPLATES;

			return isInternal
				? await postAndValidateTraxAr(route, body, templateResponseSchema)
				: await postAndValidate(route, body, templateResponseSchema);
		},
		onSuccess: (result) => {
			if (result.success) {
				showSuccess("Template created successfully.");
				void queryClient.invalidateQueries({ queryKey: ["templates"] });
			} else {
				showError(result.error ?? "Failed to create template.");
			}
		},
		onError: () => {
			showError("Failed to create template. Please try again later.");
		},
	});
};

export const useUpdateTemplate = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useNotificationActions();

	return useMutation({
		mutationFn: async ({
			templateId,
			body,
		}: {
			templateId: number;
			body: TemplateUpdateBody;
		}): Promise<ApiResult<TemplateResponse>> => {
			const isInternal = getIsInternal();
			const baseRoute = isInternal
				? apiRoutes.UPDATETEMPLATE
				: apiRoutes.EXTERNALUPDATETEMPLATE;
			const route = baseRoute.replace(":id", String(templateId));

			return isInternal
				? await putAndValidateTraxAr(route, body, templateResponseSchema)
				: await putAndValidate(route, body, templateResponseSchema);
		},
		onSuccess: (result) => {
			if (result.success) {
				showSuccess("Template updated successfully.");
				void queryClient.invalidateQueries({ queryKey: ["templates"] });
			} else {
				showError(result.error ?? "Failed to update template.");
			}
		},
		onError: () => {
			showError("Failed to update template. Please try again later.");
		},
	});
};
